import React, { useState, useEffect } from 'react';
import MobileAdminCard from './MobileAdminCard';
import mobileAdminApi, { Appointment, ConflictInfo, TeamMember } from '../services/mobileAdminApi';

const MobileAdmin: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(''); // Empty by default - no data shown
  const [selectedTeamMember, setSelectedTeamMember] = useState<number | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState<{
    appointmentId: number;
    conflicts: ConflictInfo[];
    appointment: any;
  } | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  // Load team members on mount
  useEffect(() => {
    const loadTeamMembers = async () => {
      try {
        const response = await mobileAdminApi.getTeamMembers();
        setTeamMembers(response.teamMembers);
      } catch (err) {
        console.error('Failed to load team members:', err);
        showToast('Ekip üyeleri yüklenemedi', 'error');
      }
    };

    loadTeamMembers();
  }, []);

  // Fetch appointments
  const fetchAppointments = async (useCache: boolean = true) => {
    // Don't fetch if no date is selected
    if (!selectedDate) {
      setAppointments([]);
      return;
    }

    try {
      setError(null);
      if (!useCache) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      const response = await mobileAdminApi.getAppointments(
        selectedDate,
        selectedTeamMember,
        useCache
      );
      setAppointments(response.appointments);

      // Haptic feedback on mobile
      if ('vibrate' in navigator && !useCache) {
        navigator.vibrate(50);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Bilinmeyen bir hata oluştu';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch when date or team member changes
  useEffect(() => {
    fetchAppointments(true);
  }, [selectedDate, selectedTeamMember]);

  // Toast notification
  const showToast = (message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Handle confirm
  const handleConfirm = async (id: number, forceConfirm: boolean = false) => {
    try {
      setProcessingId(id);
      setError(null);

      const response = await mobileAdminApi.confirmAppointment(id, forceConfirm);

      if (response.conflict && response.conflicts) {
        // Show conflict modal
        setConflictData({
          appointmentId: id,
          conflicts: response.conflicts,
          appointment: response.appointment,
        });
        setShowConflictModal(true);
      } else if (response.success) {
        showToast('✅ Randevu başarıyla onaylandı!', 'success');
        
        // Haptic feedback
        if ('vibrate' in navigator) {
          navigator.vibrate([50, 100, 50]);
        }

        // Refresh appointments
        await fetchAppointments(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Onaylama başarısız oldu';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  // Handle reject
  const handleReject = async (id: number) => {
    try {
      setProcessingId(id);
      setError(null);

      const response = await mobileAdminApi.rejectAppointment(id);

      if (response.success) {
        showToast('❌ Randevu başarıyla iptal edildi!', 'success');
        
        // Haptic feedback
        if ('vibrate' in navigator) {
          navigator.vibrate(100);
        }

        // Refresh appointments
        await fetchAppointments(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'İptal işlemi başarısız oldu';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setProcessingId(null);
    }
  };

  // Handle force confirm (when there's a conflict)
  const handleForceConfirm = async () => {
    if (!conflictData) return;

    setShowConflictModal(false);
    await handleConfirm(conflictData.appointmentId, true);
    setConflictData(null);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchAppointments(false);
  };

  // Format date for display
  const formatDateDisplay = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    
    // Check if it's today
    if (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    ) {
      return '📅 Bugün';
    }

    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  // Get status counts
  const getStatusCounts = () => {
    return {
      pending: appointments.filter((a) => a.status === 'pending').length,
      confirmed: appointments.filter((a) => a.status === 'confirmed').length,
      completed: appointments.filter((a) => a.status === 'completed').length,
      cancelled: appointments.filter((a) => a.status === 'cancelled').length,
    };
  };

  const statusCounts = getStatusCounts();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 shadow-md border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 py-4 max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <span>💅</span>
            <span>Sheva Studio</span>
          </h1>

          {/* Date Picker */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 relative">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                placeholder="Tarih seçin"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                style={{ 
                  minHeight: '44px',
                  colorScheme: 'light'
                }}
              />
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || !selectedDate}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white p-2.5 rounded-lg transition-colors duration-200 min-w-[44px] min-h-[44px] flex items-center justify-center active:scale-95 transform flex-shrink-0"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label="Yenile"
            >
              {isRefreshing ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              )}
            </button>
          </div>

          {/* Team Member Filter */}
          <div className="mb-3">
            <select
              value={selectedTeamMember || ''}
              onChange={(e) => setSelectedTeamMember(e.target.value ? parseInt(e.target.value) : null)}
              disabled={!selectedDate}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm disabled:bg-gray-200 dark:disabled:bg-gray-600 disabled:cursor-not-allowed"
              style={{ minHeight: '44px' }}
            >
              <option value="">👥 Tüm Ekip Üyeleri</option>
              {teamMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name} {member.specialization ? `- ${member.specialization}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Status Summary - Only show if date is selected */}
          {selectedDate && (
            <div className="grid grid-cols-4 gap-2 text-center text-xs">
              <div className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 py-2 rounded-lg">
                <div className="font-bold text-lg">{statusCounts.pending}</div>
                <div>Bekleyen</div>
              </div>
              <div className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 py-2 rounded-lg">
                <div className="font-bold text-lg">{statusCounts.confirmed}</div>
                <div>Onaylı</div>
              </div>
              <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 py-2 rounded-lg">
                <div className="font-bold text-lg">{statusCounts.completed}</div>
                <div>Tamamlandı</div>
              </div>
              <div className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 py-2 rounded-lg">
                <div className="font-bold text-lg">{statusCounts.cancelled}</div>
                <div>İptal</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-4 max-w-2xl mx-auto pb-safe">
        {/* Date Display - Only show if date is selected */}
        {selectedDate && (
          <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
            {formatDateDisplay(selectedDate)}
            {selectedTeamMember && (
              <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                ({teamMembers.find(m => m.id === selectedTeamMember)?.name})
              </span>
            )}
          </h2>
        )}

        {/* No Date Selected State */}
        {!selectedDate && !isLoading && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-6xl mb-4">📅</div>
            <p className="text-gray-600 dark:text-gray-400 text-lg font-medium mb-2">
              Tarih Seçin
            </p>
            <p className="text-gray-500 dark:text-gray-500 text-sm">
              Randevuları görüntülemek için yukarıdan bir tarih seçin
            </p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && selectedDate && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Randevular yükleniyor...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && selectedDate && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
            <button
              onClick={() => fetchAppointments(false)}
              className="mt-2 text-red-600 dark:text-red-400 font-medium text-sm hover:underline"
            >
              Tekrar dene
            </button>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && selectedDate && appointments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="text-6xl mb-4">📅</div>
            <p className="text-gray-600 dark:text-gray-400 text-lg font-medium mb-2">
              Randevu bulunamadı
            </p>
            <p className="text-gray-500 dark:text-gray-500 text-sm">
              {selectedTeamMember 
                ? 'Bu ekip üyesi için bu tarihte randevu yok'
                : 'Bu tarih için henüz randevu yok'}
            </p>
          </div>
        )}

        {/* Appointments List */}
        {!isLoading && !error && selectedDate && appointments.length > 0 && (
          <div className="space-y-4">
            {appointments.map((appointment) => (
              <MobileAdminCard
                key={appointment.id}
                appointment={appointment}
                onConfirm={handleConfirm}
                onReject={handleReject}
                isLoading={processingId === appointment.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed bottom-4 left-4 right-4 max-w-md mx-auto p-4 rounded-lg shadow-lg z-50 animate-slide-up ${
            toast.type === 'success'
              ? 'bg-green-600 text-white'
              : toast.type === 'error'
              ? 'bg-red-600 text-white'
              : 'bg-blue-600 text-white'
          }`}
        >
          <p className="font-medium">{toast.message}</p>
        </div>
      )}

      {/* Conflict Modal */}
      {showConflictModal && conflictData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <span>⚠️</span>
              <span>Çakışma Uyarısı</span>
            </h3>

            <div className="mb-4">
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                <strong>Randevu #{conflictData.appointmentId}</strong>
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                {conflictData.appointment?.customerName} - {conflictData.appointment?.time}
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Ekip Üyesi: {conflictData.appointment?.teamMember}
              </p>

              <p className="font-semibold text-gray-900 dark:text-white mb-2">
                Çakışan Onaylı Randevular:
              </p>
              <ul className="space-y-2 mb-4">
                {conflictData.conflicts.map((conflict) => (
                  <li
                    key={conflict.id}
                    className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg text-sm text-gray-700 dark:text-gray-300"
                  >
                    <div className="font-medium">{conflict.customerName}</div>
                    <div className="text-gray-600 dark:text-gray-400">
                      {conflict.customerPhone} - {conflict.time}
                    </div>
                  </li>
                ))}
              </ul>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                Yine de onaylamak istiyor musunuz?
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConflictModal(false);
                  setConflictData(null);
                }}
                className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 min-h-[44px]"
              >
                İptal
              </button>
              <button
                onClick={handleForceConfirm}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 min-h-[44px]"
              >
                Yine de Onayla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileAdmin;

